/**
 * ez-routing-signals.mixin.ts
 *
 * Mixin that exposes common Angular Router observables as Signals to make
 * routing-related state easy to consume inside signal-based components and
 * templates. This file provides a single exported function `EzRoutingSignalsMixin`
 * which augments a base class that implements `HasRoutingDeps` (requiring
 * `router: Router` and `activatedRoute: ActivatedRoute`).
 *
 * Provided signals (protected members):
 * - `routeSignal`: raw `router.events` as a signal
 * - `navigationEndSignal`: emits `NavigationEnd` events (initially `undefined`)
 * - `navigationStartSignal`: emits `NavigationStart` events
 * - `fragmentSignal`: current route fragment as a signal
 * - `activatedRouteData`: `ActivatedRoute.data` as a signal
 * - `activatedRouteParamsSignal`: merged path + query params as a `Map<string,string>`
 * - `queryParamsAsObjectSig`: merged params as a plain object (for templates)
 *
 * Helper methods for templates:
 * - `toSignalForTemplate(obs$)`: converts an `Observable` to a Signal
 * - `isSignalForTemplate(value)`: returns true when `value` is a Signal
 * - `currentValueForTemplate(input)`: returns the current value for reactive inputs
 *
 * Notes:
 * - Built around Angular Signals (`toSignal`, `computed`) and requires
 *   `@angular/core` / `@angular/router` support for signals (Angular 16+).
 * - Uses `rxjs` for filtering router events.
 *
 * TODO: Move this to a service?
 * Progress: todo list checked (none). Next: short recommendation and migration notes.

    Recommendation: Use a service — put routing signals into an injectable service rather than a mixin.
    Why: Service gives DI, single shared instance, easier testing/mocking, clearer lifecycle, and simpler reuse across components. Mixins couple logic into class inheritance and are harder to test, mock, and maintain when multiple consumers need the same behavior.
    When to keep mixin: If the logic must inject component lifecycle hooks or tightly modify component internals per-component, a mixin can be convenient for local reuse.
    Implementation notes:
    Create RoutingSignalsService (providedIn: 'root') that exposes signals/observables (Angular signals or RxJS BehaviorSubject/Subject) and listens to Router events.
    Move logic from ez-routing-signals.mixin.ts into the service.
    Inject the service into components and call its API from ngOnInit / templates as needed.
    Update tests to provide/mock the service.
 */

import { ActivatedRoute, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { computed, isSignal } from '@angular/core';
import { Observable, filter, of } from 'rxjs';

import { currentValue } from '@template-ui/types/reactive-inputs/reactive-input-utils';

// ---------------------------------------------------------
//  Constructor helper
// ---------------------------------------------------------
export type Constructor<T = object> = new (...args: any[]) => T;

// ---------------------------------------------------------
//  Required shape for the base class
// ---------------------------------------------------------
export interface HasRoutingDeps {
  router: Router;
  activatedRoute: ActivatedRoute;
}

// ---------------------------------------------------------
//  EzRoutingSignalsMixin
// ---------------------------------------------------------
export function EzRoutingSignalsMixin<TBase extends Constructor>(Base: TBase)
{
  return class extends Base {

    // -----------------------------------------------------
    // Raw router.events as a signal
    // -----------------------------------------------------
    protected readonly routeSignal = toSignal((this as unknown as HasRoutingDeps).router.events);

    // -----------------------------------------------------
    // NavigationEnd as a signal
    // -----------------------------------------------------
    protected readonly navigationEndSignal = toSignal(
      (this as unknown as HasRoutingDeps).router.events.pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd)
      ),
      { initialValue: undefined }
    );

    // -----------------------------------------------------
    // NavigationStart as a signal
    // -----------------------------------------------------
    protected readonly navigationStartSignal = toSignal(
      (this as unknown as HasRoutingDeps).router.events.pipe(
        filter((e): e is NavigationStart => e instanceof NavigationStart)
      )
    );

    // -----------------------------------------------------
    // Fragment as a signal
    // -----------------------------------------------------
    protected readonly fragmentSignal = toSignal(
      (this as unknown as HasRoutingDeps).activatedRoute?.fragment ?? of(null)
    );

    // -----------------------------------------------------
    // ActivatedRoute.data as a signal
    // -----------------------------------------------------
    protected readonly activatedRouteData = toSignal((this as unknown as HasRoutingDeps).activatedRoute.data);

    // -----------------------------------------------------
    // Collect all route params (path + query)
    // -----------------------------------------------------
    private collectAllRouteParams(): Map<string, string> {
      const merged = new Map<string, string>();
      let route: ActivatedRoute | null = (this as unknown as HasRoutingDeps).router.routerState.root;

      while (route) {
        const snap = route.snapshot;

        if (snap) {
          for (const key of snap.paramMap.keys) {
            merged.set(key, snap.paramMap.get(key)!);
          }
          for (const key of snap.queryParamMap.keys) {
            merged.set(key, snap.queryParamMap.get(key)!);
          }
        }

        route = route.firstChild ?? null;
      }

      return merged;
    }

    // -----------------------------------------------------
    // ActivatedRoute params (path + query) as a signal
    // -----------------------------------------------------
    protected readonly activatedRouteParamsSignal = computed(() => {
      const nav = this.navigationEndSignal();
      if (!nav) return new Map<string, string>();
      return this.collectAllRouteParams();
    });

    // -----------------------------------------------------
    // Query params as a plain object
    // -----------------------------------------------------
    protected readonly queryParamsAsObjectSig = computed(() => {
      const map = this.activatedRouteParamsSignal() ?? new Map<string, string>();
      return Object.fromEntries(map);
    });

    // -----------------------------------------------------
    // Template helpers
    // -----------------------------------------------------
    protected toSignalForTemplate<T = unknown>(obs$: Observable<T>) {
      return toSignal<T>(obs$);
    }

    protected isSignalForTemplate(value: unknown): boolean {
      return isSignal(value);
    }

    protected currentValueForTemplate(input: unknown) {
      return currentValue(input);
    }
  };
}
